import { Error as GrpcError } from "grpc-web";
import mapGrpcErrorToString from "./utils/mapGrpcErrorToString";
import useToaster from "./useToaster";
import { useAuth } from "@/modules/auth/useAuth";
import { EGrpcErrorCode } from "@/custom-types";
import { ApolloError } from "@apollo/client";

import capitalize from "lodash/capitalize";

import { bugsnagClient } from "@/bootstrap";

export const useErrorHandlerWithoutReAuth = () => {
  const { showErrorToast: _showErrorToast } = useToaster();

  const handleGenericError = (err: string | Error, toast: boolean = true) => {
    if (err instanceof ApolloError) {
      const message = err.graphQLErrors
        .map(e => capitalize(e.message?.replace(/_/g, " ")))
        .join("\n");
      toast && _showErrorToast(message);
      return;
    } else if (typeof err === "string") {
      toast && _showErrorToast(err);
      return;
    } else if (typeof err.message === "string") {
      toast && _showErrorToast(err.message);
      return;
    }

    toast && _showErrorToast("unknown");
    return;
  };

  const handleGrpcError = (err: GrpcError, toast: boolean = true) => {
    if (bugsnagClient) {
      bugsnagClient.notify(
        new Error(
          "GRPC endpoint returned an error. Error code:" +
            err.code +
            " Error: " +
            err.message
        )
      );
    }
    if (toast) {
      _showErrorToast(mapGrpcErrorToString(err));
    }
  };

  return {
    handleGrpcError,
    handleGenericError
  };
};

const useErrorHandler = () => {
  const {
    handleGrpcError: _handleGrpcError,
    handleGenericError
  } = useErrorHandlerWithoutReAuth();
  const [, { logout }] = useAuth();

  const handleError = (err: any, toast: boolean = true) => {
    if (
      // assume it's a grpc error if there's an error.code
      typeof err.code === "number" &&
      typeof err.message === "string"
    ) {
      return handleGrpcError(err, toast);
    }

    return handleGenericError(err, toast);
  };

  const handleGrpcError = (err: GrpcError, toast: boolean = true) => {
    // handle token expiry
    if (err.code === EGrpcErrorCode.INVALID_ARGUMENT) {
      if (err.message.includes("missing token")) {
        logout();
        return;
      }
    }
    if (err.code === EGrpcErrorCode.UNAUTHENTICATED) {
      logout();
      return;
    }

    return _handleGrpcError(err, toast);
  };

  return {
    handleError
  };
};

export default useErrorHandler;
